import React, { useState, useEffect, useCallback, useRef } from 'react';
import { naytiveAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './NaytiveChats.css';

/* Naytive Apps → Messages.
 *
 * Two-pane live-chat console: conversation list (left) + thread + reply
 * composer (right). Any agent can open and reply; replies land in the founder's
 * v3 chat instantly and email them (single thread). Polls every 4s. */

const POLL_MS = 4000;

function initials(name, email) {
  const s = String(name || email || '').trim();
  if (!s) return 'U';
  const p = s.split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || s[0].toUpperCase();
}
function fullName(c) {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || c.user_email || `User ${c.user_id}`;
}
function timeShort(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }); } catch { return ''; }
}

export default function NaytiveChats() {
  const [chats, setChats] = useState([]);
  const [selId, setSelId] = useState(null);
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [docMode, setDocMode] = useState(false);
  const [docLabel, setDocLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState([]);
  const scrollRef = useRef(null);
  const lastIdRef = useRef(0);
  const fileRef = useRef(null);

  const loadChats = useCallback(async () => {
    try { const r = await naytiveAPI.listChats(); setChats(r.data?.items || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadChats(); const iv = setInterval(loadChats, POLL_MS * 2); return () => clearInterval(iv); }, [loadChats]);

  const scrollDown = (smooth) => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  // Load a selected conversation's full thread.
  const openChat = useCallback(async (id) => {
    setSelId(id);
    setMessages([]);
    lastIdRef.current = 0;
    try {
      const r = await naytiveAPI.getChatMessages(id);
      setConv(r.data?.conversation || null);
      const msgs = r.data?.messages || [];
      setMessages(msgs);
      lastIdRef.current = msgs.length ? msgs[msgs.length - 1].id : 0;
      setTimeout(() => scrollDown(false), 40);
      loadChats(); // clears unread badge
    } catch { /* ignore */ }
  }, [loadChats]);

  // Poll the open thread for new messages.
  useEffect(() => {
    if (!selId) return undefined;
    const iv = setInterval(async () => {
      try {
        const r = await naytiveAPI.getChatMessages(selId, lastIdRef.current);
        const nw = r.data?.messages || [];
        if (nw.length) {
          setMessages((prev) => { const ids = new Set(prev.map((m) => m.id)); return [...prev, ...nw.filter((m) => !ids.has(m.id))]; });
          lastIdRef.current = Math.max(lastIdRef.current, nw[nw.length - 1].id);
          setTimeout(() => scrollDown(true), 40);
        }
      } catch { /* ignore */ }
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [selId]);

  const send = async () => {
    const body = reply.trim();
    if (!body || !selId || sending) return;
    setSending(true);
    setReply('');
    const tmpId = `tmp-${messages.length}-${body.length}`;
    setMessages((p) => [...p, { id: tmpId, sender_type: 'agent', body, created_at: new Date().toISOString(), _pending: true }]);
    setTimeout(() => scrollDown(true), 20);
    try {
      const r = await naytiveAPI.sendChatReply(selId, body);
      const realId = r.data?.id;
      setMessages((p) => p.map((m) => (m.id === tmpId ? { ...m, id: realId, sender_name: r.data?.sender_name, _pending: false } : m)));
      if (realId) lastIdRef.current = Math.max(lastIdRef.current, realId);
      loadChats();
    } catch { /* keep optimistic */ } finally { setSending(false); }
  };

  const refreshThread = async () => {
    try {
      const r = await naytiveAPI.getChatMessages(selId, lastIdRef.current);
      const nw = r.data?.messages || [];
      if (nw.length) {
        setMessages((prev) => { const ids = new Set(prev.map((m) => m.id)); return [...prev, ...nw.filter((m) => !ids.has(m.id))]; });
        lastIdRef.current = Math.max(lastIdRef.current, nw[nw.length - 1].id);
      }
    } catch { /* ignore */ }
  };

  const requestDoc = async () => {
    const label = docLabel.trim();
    if (!label || !selId) return;
    setDocLabel(''); setDocMode(false);
    try { await naytiveAPI.requestChatDoc(selId, label); await refreshThread(); setTimeout(() => scrollDown(true), 40); loadChats(); } catch { /* ignore */ }
  };

  const onAgentFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file || !selId) return;
    if (file.size > 6 * 1024 * 1024) { alert('File too large (max 6 MB)'); return; }
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = String(reader.result || '').split(',')[1] || '';
      try {
        await naytiveAPI.uploadChatFile(selId, { fileBase64: base64, fileName: file.name, fileMime: file.type || 'application/octet-stream' });
        await refreshThread(); setTimeout(() => scrollDown(true), 40); loadChats();
      } catch { /* ignore */ } finally { setUploading(false); }
    };
    reader.readAsDataURL(file);
  };

  const openFile = async (key) => {
    try { const r = await naytiveAPI.getChatFileUrl(selId, key); if (r.data?.url) window.open(r.data.url, '_blank', 'noopener'); } catch { /* ignore */ }
  };

  const openNewChat = async () => {
    setNewChatOpen(true); setUserQuery('');
    try { const r = await naytiveAPI.searchChatUsers(''); setUserResults(r.data?.items || []); } catch { setUserResults([]); }
  };
  useEffect(() => {
    if (!newChatOpen) return undefined;
    const t = setTimeout(async () => {
      try { const r = await naytiveAPI.searchChatUsers(userQuery.trim()); setUserResults(r.data?.items || []); } catch { setUserResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [userQuery, newChatOpen]);
  const startChatWith = async (u) => {
    try {
      const r = await naytiveAPI.startChat(u.id);
      const cid = r.data?.id;
      setNewChatOpen(false);
      if (cid) { await loadChats(); openChat(cid); }
    } catch { /* ignore */ }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main nc-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">MESSAGES</h1>
            <p className="page-subtitle">Live chat with founders — any agent can reply. New replies email the founder in one thread.</p>
          </div>
        </div>

        <div className="nc-shell">
          {/* conversation list */}
          <aside className="nc-list">
            <div className="nc-list-head">
              <span>Conversations</span>
              <button type="button" className="nc-newbtn" onClick={() => (newChatOpen ? setNewChatOpen(false) : openNewChat())}>
                {newChatOpen ? 'Cancel' : '＋ New chat'}
              </button>
            </div>
            {newChatOpen ? (
              <div className="nc-newchat">
                <input className="nc-search" autoFocus value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search a founder by name or email…" />
                {userResults.length === 0 ? (
                  <div className="nc-empty">No users found.</div>
                ) : userResults.map((u) => (
                  <button key={u.id} type="button" className="nc-item" onClick={() => startChatWith(u)}>
                    <div className="nc-avatar">{initials([u.first_name, u.last_name].filter(Boolean).join(' '), u.email)}</div>
                    <div className="nc-item-main">
                      <div className="nc-item-top"><span className="nc-item-name">{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.email}</span></div>
                      <div className="nc-item-sub"><span className="nc-item-preview">{u.email}</span></div>
                    </div>
                  </button>
                ))}
              </div>
            ) : chats.length === 0 ? (
              <div className="nc-empty">No conversations yet.</div>
            ) : chats.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`nc-item${selId === c.id ? ' active' : ''}`}
                onClick={() => openChat(c.id)}
              >
                <div className="nc-avatar">{initials(fullName(c), c.user_email)}</div>
                <div className="nc-item-main">
                  <div className="nc-item-top">
                    <span className="nc-item-name">{fullName(c)}</span>
                    <span className="nc-item-time">{timeShort(c.last_message_at)}</span>
                  </div>
                  <div className="nc-item-sub">
                    <span className="nc-item-preview">{c.title ? <strong style={{ color: '#475467', fontWeight: 600 }}>{c.title}</strong> : null}{c.title ? ' · ' : ''}{c.last_sender_type === 'agent' ? 'You: ' : ''}{c.last_body || '—'}</span>
                    {c.unread > 0 && <span className="nc-badge">{c.unread}</span>}
                  </div>
                </div>
              </button>
            ))}
          </aside>

          {/* thread */}
          <section className="nc-thread">
            {!selId ? (
              <div className="nc-thread-empty">Select a conversation to reply.</div>
            ) : (
              <>
                <div className="nc-thread-head">
                  <div className="nc-avatar sm">{conv ? initials(fullName(conv), conv.user_email) : 'U'}</div>
                  <div>
                    <div className="nc-thread-name">{conv ? fullName(conv) : 'Conversation'}{conv?.title ? <span style={{ fontWeight: 400, color: '#667085' }}> — {conv.title}</span> : null}</div>
                    <div className="nc-thread-sub">{conv?.user_email}{conv?.phone ? ` · ${conv.phone}` : ''}</div>
                  </div>
                </div>
                {conv?.context && (
                  <div className="nc-context">
                    <button type="button" className="nc-context-toggle" onClick={() => setShowContext((v) => !v)}>
                      <span>🤖 Ask Naytive history {conv.source === 'advisor' ? '(handed off from the AI)' : ''}</span>
                      <span>{showContext ? '▲ Hide' : '▼ Show'}</span>
                    </button>
                    {showContext && <pre className="nc-context-body">{conv.context}</pre>}
                  </div>
                )}
                <div className="nc-msgs" ref={scrollRef}>
                  {messages.map((m) => {
                    if (m.kind === 'doc_request') {
                      const meta = m.meta || {};
                      const fulfilled = meta.status === 'fulfilled';
                      return (
                        <div key={m.id} className="nc-row out">
                          <div className={`nc-docreq${fulfilled ? ' done' : ''}`}>
                            <div className="nc-docreq-label">📄 Requested: {meta.label || 'Document'}</div>
                            {m.body ? <div className="nc-docreq-note">{m.body}</div> : null}
                            <div className="nc-docreq-status">{fulfilled ? `✓ Uploaded${meta.fileName ? ` · ${meta.fileName}` : ''}` : 'Awaiting upload…'}</div>
                            {fulfilled && meta.fileKey && <button className="nc-att-btn" onClick={() => openFile(meta.fileKey)}>View document</button>}
                          </div>
                          <div className="nc-meta">{m.sender_name ? `${m.sender_name} · ` : ''}{timeShort(m.created_at)}</div>
                        </div>
                      );
                    }
                    return (
                      <div key={m.id} className={`nc-row ${m.sender_type === 'agent' ? 'out' : 'in'}`}>
                        <div className={`nc-bubble${m._pending ? ' pending' : ''}`}>
                          {m.body}
                          {Array.isArray(m.attachments) && m.attachments.map((a, i) => (
                            <button key={i} className="nc-att-btn" onClick={() => openFile(a.key)}>📎 {a.name || 'Attachment'}</button>
                          ))}
                        </div>
                        <div className="nc-meta">
                          {m.sender_type === 'agent' && m.sender_name ? `${m.sender_name} · ` : ''}{timeShort(m.created_at)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="nc-composer">
                  {docMode ? (
                    <>
                      <input
                        className="nc-doc-input"
                        value={docLabel}
                        onChange={(e) => setDocLabel(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); requestDoc(); } }}
                        placeholder="Document to request (e.g. PAN Card, Bank statement)"
                        autoFocus
                      />
                      <button type="button" onClick={requestDoc} disabled={!docLabel.trim()}>Request</button>
                      <button type="button" className="nc-ghost" onClick={() => { setDocMode(false); setDocLabel(''); }}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <input ref={fileRef} type="file" hidden onChange={onAgentFile} accept="image/*,application/pdf" />
                      <button type="button" className="nc-icon" title="Attach a file" onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}>📎</button>
                      <button type="button" className="nc-icon" title="Request a document" onClick={() => setDocMode(true)}>＋Doc</button>
                      <textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                        placeholder="Type your reply…  (Enter to send)"
                        rows={2}
                      />
                      <button type="button" onClick={send} disabled={!reply.trim() || sending}>Send</button>
                    </>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
